import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { ToolbarComponent } from '../../../shared/components/toolbar/toolbar.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ICommunity } from '../../model/community.model';
import { CommunityService } from '../../services/community.service';

@Component({
    selector: 'clp-community-list',
    styleUrls: ['./communityList.component.scss'],
    templateUrl: './communityList.component.html',
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule,
        ToolbarComponent
    ],
    providers: [
        CommunityService,
        ToastrService
    ]
})
export class CommunityListComponent implements OnInit {
    private communityService = inject(CommunityService);
    private router = inject(Router);
    private toastService = inject(ToastService);
    private translate = inject(TranslateService);

    communities$!: Observable<ICommunity[]>;
    searchTerm = '';
    minimumMembersFilter: 'all' | '1' | '5' | '10' = 'all';
    communityToDelete: ICommunity | null = null;
    deleting = false;

    ngOnInit(): void {
        this.communities$ = this.communityService.getCommunities();
    }

    editCommunity(id: string | undefined): void {
        this.router.navigate(['updateCommunity', id]);
    }

    viewCommunitySubscribers(id: string | undefined): void {
        if (!id) {
            return;
        }

        this.router.navigate(['communitySubscribers', id]);
    }

    openDeleteConfirmation(community: ICommunity): void {
        if (!community.id) {
            return;
        }

        this.communityToDelete = community;
    }

    closeDeleteConfirmation(): void {
        if (this.deleting) {
            return;
        }

        this.communityToDelete = null;
    }

    confirmDeleteCommunity(): void {
        const id = this.communityToDelete?.id;
        if (!id) {
            this.closeDeleteConfirmation();
            return;
        }

        this.deleting = true;
        this.communityService.deleteCommunity(id).subscribe({
            next: (community) => {
                this.deleting = false;
                if (community) {
                    this.communityToDelete = null;
                    this.toastService.show(this.translate.instant('community.delete.successMessage'), '');
                    this.communities$ = this.communityService.getCommunities();
                }
            },
            error: () => {
                this.deleting = false;
            }
        });
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        this.closeDeleteConfirmation();
    }

    onSearchChange(event: Event): void {
        this.searchTerm = (event.target as HTMLInputElement).value;
    }

    onMinimumMembersFilterChange(event: Event): void {
        this.minimumMembersFilter = (event.target as HTMLSelectElement).value as 'all' | '1' | '5' | '10';
    }

    getFilteredCommunities(communities: ICommunity[]): ICommunity[] {
        const search = this.searchTerm.trim().toLowerCase();
        const minMembers = this.minimumMembersFilter === 'all'
            ? null
            : Number(this.minimumMembersFilter);

        return communities.filter((community) => {
            const name = (community.name ?? '').toLowerCase();
            const subscriptionName = (community.subscriptionName ?? '').toLowerCase();
            const membersCount = community.members?.length ?? 0;
            const matchesSearch = !search || name.includes(search) || subscriptionName.includes(search);
            const matchesMembers = minMembers === null || membersCount >= minMembers;

            return matchesSearch && matchesMembers;
        });
    }

    getTotalMembers(communities: ICommunity[]): number {
        return communities.reduce((sum, community) => sum + (community.members?.length ?? 0), 0);
    }

    getAverageMembers(communities: ICommunity[]): number {
        if (!communities.length) {
            return 0;
        }

        return this.getTotalMembers(communities) / communities.length;
    }
}
